import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Button } from '@mui/material';
import NightAuditModal from '../../Finance/components/NightAuditModal';
import { useCurrency, formatCurrency } from '../../../utils/currency';

const formatAuditDate = (dateVal) => {
    if (!dateVal) return 'Pending First Audit';
    try {
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? String(dateVal) : d.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).replace(',', ',');
    } catch {
        return String(dateVal);
    }
};

export default function NightAuditWidget({ data }) {
    const { currency } = useCurrency();
    const [auditModalOpen, setAuditModalOpen] = useState(false);

    // Extract actual API data with zero-safe defaults
    const status = data?.status || 'Pending';
    const isCompleted = status === 'Completed';
    const lastAuditDate = formatAuditDate(data?.lastAudit);
    const balance = formatCurrency(Number(data?.balance) || 0, currency);
    const unpostedFolios = `${Number(data?.unpostedFolios) || 0} unbilled`;
    
    const getFormattedToday = () => {
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };
    const cycleDate = data?.cycleDate || getFormattedToday();

    return (
        <>
            <Card sx={{ 
                borderRadius: 3, 
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
                border: '1px solid #f1f5f9',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    {/* Header */}
                    <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.2 }}>
                                Night Audit Engine
                            </Typography>
                            <Box sx={{ 
                                bgcolor: isCompleted ? '#ecfdf5' : '#fef3c7', 
                                border: `1px solid ${isCompleted ? '#a7f3d0' : '#fde68a'}`,
                                color: isCompleted ? '#059669' : '#d97706', 
                                px: 1, 
                                py: 0.2, 
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                fontWeight: 700 
                            }}>
                                {status}
                            </Box>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                            Daily financial ledger rollover
                        </Typography>
                    </Box>

                    {/* Cycle Notice Box */}
                    <Box sx={{ 
                        p: 1.5, 
                        borderRadius: '8px', 
                        bgcolor: '#f8fafc', 
                        border: '1px solid #f1f5f9',
                        my: 2
                    }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.76rem', lineHeight: 1.5 }}>
                            Cycle: {cycleDate}. Pending end-of-day room posting and trial balance reconciliation before advancing business date.
                        </Typography>
                    </Box>

                    {/* Key Metrics Rows */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                Last Audit
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>
                                {lastAuditDate}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                Current Balance
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.82rem' }}>
                                {balance}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                Unposted Folios
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#ef4444', fontSize: '0.8rem' }}>
                                {unpostedFolios}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Execute Audit Button */}
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => setAuditModalOpen(true)}
                        sx={{
                            bgcolor: '#0f172a',
                            color: '#ffffff',
                            borderRadius: '8px',
                            py: 1.1,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#1e293b',
                                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.2)'
                            }
                        }}
                    >
                        Execute End-of-Day Audit
                    </Button>
                </CardContent>
            </Card>

            <NightAuditModal
                open={auditModalOpen}
                onClose={() => setAuditModalOpen(false)}
                onSuccess={() => {
                    setAuditModalOpen(false);
                    window.dispatchEvent(new Event('nightAuditCompleted'));
                }}
            />
        </>
    );
}
